// Enterprise Dashboard — integration hub with live API data (no mock seeding)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Settings,
  Users,
  Bell,
  Search,
  Globe,
  Folder,
  TrendingUp,
  Activity,
  Zap,
  Database,
  Cloud,
  Mic,
  Plus,
  RefreshCw,
  AlertCircle,
  Network,
  Star,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { useTheme } from '@/hooks/useTheme';
import { wsService } from '../services/websocket.service';
import { usePerformance } from '../services/performance.service';
import { useAuthStore, useHasHydrated } from '@/stores/authStore';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { PromptDialog } from '../components/ui/Modal';
import { api } from '@/services/client';
import {
  tabService,
  type TabGroup,
  type TabNotification,
  type SharedSession,
  type Collaborator,
} from '@/services/tabs';
import type { ApiBrowserTab } from '@/types';
import { toast } from 'sonner';
import { notifyExtensionTabAction, requestExtensionCommandPoll } from '@/services/extensionBridge';
import { RechartsBarChart } from '@/components/visualizations/RechartsCharts';

const TOP_SITES_LIMIT = 6;

const WS_SESSION_KEY = 'khiimori_workspace_session_id';

interface DashboardTab {
  id: string;
  title: string;
  url: string;
  favicon?: string;
  groupId?: string;
  groupName?: string;
  groupColor?: string;
}

interface ActivityItem {
  id: string;
  action: string;
  /** ISO timestamp or pre-formatted relative label from API */
  timestamp: string;
  timeLabel?: string;
}

interface AnalyticsSnapshot {
  productivityScore: number;
  focusTimeSeconds: number;
  tabsOpened: number;
  topSites: Array<{ domain: string; count: number }>;
  trends: Array<{ label: string; value: number }>;
  sessionTotal: number;
  insights: string[];
}

function formatActivityTime(timestamp: string, timeLabel?: string): string {
  if (timeLabel?.trim()) return timeLabel;
  if (!timestamp?.trim()) return '';
  const parsed = new Date(timestamp);
  if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
  return timestamp;
}

/**
 * Render a timestamp as a compact relative label: "just now", "5m ago",
 * "3h ago", "2d ago", falling back to a short date for anything older
 * than a week. Returns '' for unparseable input.
 */
function relativeTime(timestamp: string): string {
  if (!timestamp?.trim()) return '';
  const t = new Date(timestamp);
  if (Number.isNaN(t.getTime())) return '';
  const diffMs = Date.now() - t.getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return t.toLocaleDateString();
}

function formatVitalValue(label: string, raw: number): string {
  if (!Number.isFinite(raw)) return '—';
  if (label === 'CLS') return raw.toFixed(3);
  if (label === 'FPS') return String(Math.round(raw));
  if (raw < 100) return raw.toFixed(1);
  return String(Math.round(raw));
}

function countGroupedTabs(tabIds: Set<string>, groups: TabGroup[]): number {
  const grouped = new Set<string>();
  for (const group of groups) {
    for (const tab of group.tabs ?? []) {
      if (tabIds.has(tab.id)) grouped.add(tab.id);
    }
  }
  return grouped.size;
}

function uniqueGroupsByName(groups: TabGroup[]): TabGroup[] {
  const seen = new Set<string>();
  return groups.filter((group) => {
    const key = group.name.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

interface SessionRow {
  id: string;
  name: string;
  is_active?: boolean;
  tab_count?: number;
}

function mapTabsWithGroups(apiTabs: ApiBrowserTab[], groups: TabGroup[]): DashboardTab[] {
  const tabToGroup = new Map<string, TabGroup>();
  for (const group of groups) {
    for (const tab of group.tabs ?? []) {
      tabToGroup.set(tab.id, group);
    }
  }
  return apiTabs.map((tab) => {
    const group = tabToGroup.get(tab.id);
    return {
      id: tab.id,
      title: tab.title || '(untitled)',
      url: tab.url || '',
      favicon: tab.favicon,
      groupId: group?.id,
      groupName: group?.name,
      groupColor: group?.color,
    };
  });
}

function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

function domainBreakdownFromTabs(tabs: DashboardTab[]): Array<{ domain: string; count: number }> {
  const counts = new Map<string, number>();
  for (const tab of tabs) {
    const domain = domainFromUrl(tab.url) || 'unknown';
    counts.set(domain, (counts.get(domain) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([domain, count]) => ({ domain, count }))
    .sort((a, b) => b.count - a.count);
}

const EnterpriseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'team'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [tabs, setTabs] = useState<DashboardTab[]>([]);
  const [groups, setGroups] = useState<TabGroup[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sharedWorkspaces, setSharedWorkspaces] = useState<SharedSession[]>([]);
  const [notifications, setNotifications] = useState<TabNotification[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [bookmarkedTabIds, setBookmarkedTabIds] = useState<Set<string>>(new Set());
  const [bookmarkingTabId, setBookmarkingTabId] = useState<string | null>(null);

  const [promptAction, setPromptAction] = useState<'workspace' | 'group' | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSnapshot | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const { metrics, score, isHealthy } = usePerformance();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useHasHydrated();

  const displayName = user?.full_name || user?.name || user?.email?.split('@')[0] || 'User';

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      let sid = sessionStorage.getItem(WS_SESSION_KEY);
      const sessionsRes = await api.get<{ sessions: SessionRow[] }>('/sessions');
      const sessionRows = sessionsRes.data.sessions ?? [];
      if (!sid) {
        const active = sessionRows.find((s) => s.is_active) ?? sessionRows[0];
        sid = active?.id ?? null;
        if (sid) sessionStorage.setItem(WS_SESSION_KEY, sid);
      }
      setSessionId(sid);
      setSessions(sessionRows);

      const [
        tabsResult,
        groupsResult,
        notifResult,
        sharedResult,
        collabResult,
        activityResult,
        bookmarksResult,
      ] = await Promise.all([
        sid ? tabService.getTabsBySession(sid, 1, 200) : Promise.resolve({ tabs: [] as ApiBrowserTab[], total: 0 }),
        sid ? tabService.getTabGroups(sid).catch(() => ({ groups: [] as TabGroup[] })) : Promise.resolve({ groups: [] as TabGroup[] }),
        tabService.getNotifications().catch(() => [] as TabNotification[]),
        tabService.getSharedWorkspaces().catch(() => [] as SharedSession[]),
        tabService.getCollaborators().catch(() => [] as Collaborator[]),
        tabService.getCollaborationHubActivity().catch(() => []),
        api.get<{ bookmarks: { tab_id?: string }[] }>('/bookmarks').catch(() => ({ data: { bookmarks: [] } })),
      ]);

      const groupList = groupsResult.groups ?? [];
      setTabs(mapTabsWithGroups(tabsResult.tabs, groupList));
      setGroups(groupList);
      setNotifications(notifResult);
      setSharedWorkspaces(sharedResult);
      setCollaborators(collabResult);

      const bookmarkIds = new Set(
        (bookmarksResult.data.bookmarks ?? [])
          .map((b) => b.tab_id)
          .filter((id): id is string => Boolean(id)),
      );
      setBookmarkedTabIds(bookmarkIds);

      let activityItems: ActivityItem[] = (activityResult ?? []).map((row, index) => ({
        id: `collab-${index}-${row.time}`,
        action: [row.user, row.action, row.target].filter(Boolean).join(' — '),
        timestamp: row.time,
        timeLabel: row.time && Number.isNaN(Date.parse(row.time)) ? row.time : undefined,
      }));

      if (activityItems.length === 0 && sid) {
        const timeline = await tabService.getTabTimeline(sid, 'today').catch(() => []);
        activityItems = timeline.slice(0, 25).map((entry) => ({
          id: entry.id,
          action: `${entry.tabTitle || domainFromUrl(entry.tabUrl) || 'Tab'} — ${entry.action.replace(/_/g, ' ')}`,
          timestamp: entry.time,
        }));
      }
      setActivities(activityItems);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [prodRes, tabsRes, trendsRes, sessionsRes] = await Promise.allSettled([
        api.get<Record<string, unknown>>('/productivity/stats', { params: { period: 'week' } }),
        api.get<Record<string, unknown>>('/analytics/enhanced/tabs', { params: { period: 'week' } }),
        api.get<Record<string, unknown>>('/analytics/enhanced/trends', { params: { period: 'week' } }),
        api.get<Record<string, unknown>>('/analytics/enhanced/sessions', { params: { period: 'week' } }),
      ]);

      const prod = prodRes.status === 'fulfilled' ? prodRes.value.data : {};
      const tabsData = tabsRes.status === 'fulfilled' ? tabsRes.value.data : {};
      const trendsData = trendsRes.status === 'fulfilled' ? trendsRes.value.data : {};
      const sessionsData = sessionsRes.status === 'fulfilled' ? sessionsRes.value.data : {};

      const topSitesRaw = Array.isArray(tabsData.top_sites) ? tabsData.top_sites : [];
      const trendsRaw = Array.isArray(trendsData.trends) ? trendsData.trends : [];

      setAnalytics({
        productivityScore: Number(prod.productivity_score ?? 0),
        focusTimeSeconds: Number(prod.focus_time_seconds ?? 0),
        tabsOpened: Number(prod.tabs_opened ?? prod.tabs_managed ?? 0),
        topSites: topSitesRaw.map((row: Record<string, unknown>) => ({
          domain: String(row.domain ?? row.site ?? row.name ?? 'unknown'),
          count: Number(row.count ?? row.visits ?? row.tabs ?? 0),
        })),
        trends: trendsRaw.map((row: Record<string, unknown>, i: number) => ({
          label: String(row.label ?? row.date ?? `Day ${i + 1}`),
          value: Number(row.value ?? row.tabs_opened ?? 0),
        })),
        sessionTotal: Number(sessionsData.total_sessions ?? 0),
        insights: Array.isArray(tabsData.insights) ? tabsData.insights.map(String) : [],
      });
    } catch {
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasHydrated) return;

    let cancelled = false;

    const syncConnection = (connected: boolean, detail?: string | null) => {
      if (cancelled) return;
      setWsConnected(connected);
      if (connected) {
        setConnectionError(null);
      } else {
        setConnectionError(detail || 'Live sync offline — dashboard still loads from API');
      }
    };

    const onStatus = (payload: { connected?: boolean; error?: string; hint?: string }) => {
      if (payload.connected) {
        syncConnection(true);
        return;
      }
      const detail =
        payload.hint ||
        (payload.error === 'no_token'
          ? 'Sign in to enable live sync'
          : payload.error === 'token_expired'
            ? 'Session expired — sign in again'
            : payload.error === 'max_reconnect_attempts'
              ? 'Could not reach sync server — is the API running on :8080?'
              : null);
      syncConnection(false, detail);
    };

    wsService.on('connection_status', onStatus);

    const init = async () => {
      if (!isAuthenticated) {
        syncConnection(false, 'Sign in to enable live sync');
        if (!cancelled) await loadDashboard();
        return;
      }
      try {
        await wsService.connect();
        if (!cancelled) syncConnection(wsService.isConnected());
      } catch {
        if (!cancelled) syncConnection(false);
      }
      if (!cancelled) await loadDashboard();
    };

    void init();
    const interval = setInterval(() => {
      if (!cancelled) setWsConnected(wsService.isConnected());
    }, 5000);

    return () => {
      cancelled = true;
      wsService.off('connection_status', onStatus);
      clearInterval(interval);
    };
  }, [loadDashboard, hasHydrated, isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'analytics') void loadAnalytics();
  }, [activeTab, loadAnalytics]);

  const stats = useMemo(() => {
    const tabIdSet = new Set(tabs.map((t) => t.id));
    const groupedTabCount = countGroupedTabs(tabIdSet, groups);
    const unreadNotifications = notifications.filter((n) => !n.read).length;
    return {
      totalTabs: tabs.length,
      groupedTabs: groupedTabCount,
      activeGroups: groups.length,
      activeWorkspaces: sharedWorkspaces.length,
      sessionCount: sessions.length,
      unreadNotifications,
      performanceScore: score,
      connectionStatus: wsConnected ? 'connected' : 'disconnected',
    };
  }, [tabs, groups, sharedWorkspaces, sessions, notifications, score, wsConnected]);

  const filteredTabs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tabs;
    return tabs.filter(
      (tab) =>
        tab.title.toLowerCase().includes(q) ||
        tab.url.toLowerCase().includes(q) ||
        tab.groupName?.toLowerCase().includes(q),
    );
  }, [tabs, searchQuery]);

  const handleMarkNotificationRead = async (id: string) => {
    try {
      await tabService.markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleBookmarkTab = async (tabId: string) => {
    if (bookmarkedTabIds.has(tabId)) {
      toast.info('Already bookmarked');
      return;
    }
    setBookmarkingTabId(tabId);
    try {
      await api.post('/bookmarks', { tab_id: tabId });
      setBookmarkedTabIds((prev) => new Set([...prev, tabId]));
      toast.success('Tab saved to bookmarks');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to bookmark tab');
    } finally {
      setBookmarkingTabId(null);
    }
  };

  const handleCreateWorkspace = async (name: string) => {
    setPromptAction(null);
    try {
      await tabService.createWorkspace(name.trim() || 'New workspace');
      toast.success('Workspace created');
      await loadDashboard();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create workspace');
    }
  };

  const handleCreateGroup = async (name: string) => {
    setPromptAction(null);
    if (!sessionId) {
      toast.error('No active session — sync tabs first');
      return;
    }
    try {
      const tabIds = tabs.map((t) => t.id);
      await tabService.createTabGroup(name.trim() || 'New group', tabIds, sessionId);
      toast.success('Tab group created');
      await loadDashboard();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create group');
    }
  };

  const handleAutoOrganize = () => {
    notifyExtensionTabAction('auto_organize');
    requestExtensionCommandPoll();
    toast.success('Auto-organize sent to browser extension');
  };

  const handleSyncAll = async () => {
    requestExtensionCommandPoll();
    toast.promise(loadDashboard(), {
      loading: 'Syncing…',
      success: 'Dashboard refreshed',
      error: 'Sync failed',
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="flex items-center gap-3 text-violet-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span>Loading dashboard…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <header className="sticky top-0 z-40 border-b border-khiimori-border/10 bg-khiimori-bg-secondary/80 backdrop-blur-xl">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              type="text"
              placeholder="Search tabs, groups, workspaces..."
              leftIcon={<Search className="h-4 w-4" />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full max-w-md border-khiimori-border/10 bg-khiimori-bg-elevated/5 text-sm text-khiimori-text-primary placeholder:text-khiimori-text-muted focus:border-violet-500/50 sm:w-96"
            />

            <div className="flex flex-wrap items-center gap-2">
              {/* Status pill — sync */}
              <div
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
                  wsConnected
                    ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
                    : 'border-amber-500/20 bg-amber-500/10 text-amber-300'
                }`}
                title="WebSocket live sync for collaboration and real-time updates"
              >
                <span className="relative flex h-2 w-2">
                  <span
                    className={`absolute inline-flex h-full w-full animate-ping rounded-full ${
                      wsConnected ? 'bg-emerald-400' : 'bg-amber-400'
                    } opacity-75`}
                  />
                  <span
                    className={`relative inline-flex h-2 w-2 rounded-full ${
                      wsConnected ? 'bg-emerald-400' : 'bg-amber-400'
                    }`}
                  />
                </span>
                <Network className="h-3.5 w-3.5" />
                <span>Live sync {wsConnected ? 'On' : 'Off'}</span>
              </div>
              {/* Status pill — perf */}
              <div
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
                  isHealthy
                    ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
                    : 'border-amber-500/20 bg-amber-500/10 text-amber-300'
                }`}
                title="Browser performance score (Core Web Vitals)"
              >
                <TrendingUp className="h-3.5 w-3.5" />
                <span>Perf {score}%</span>
              </div>
              {!wsConnected && connectionError ? (
                <div className="hidden items-center gap-1.5 rounded-md bg-khiimori-bg-elevated/10 px-2.5 py-1.5 text-[11px] text-khiimori-text-muted lg:flex">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  <span className="max-w-xs truncate">{connectionError}</span>
                </div>
              ) : null}
              <div className="flex items-center gap-2 rounded-full border border-khiimori-border/10 bg-khiimori-bg-elevated/5 py-1 pl-1 pr-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-sm font-semibold text-white">
                  {displayName[0]?.toUpperCase() || 'U'}
                </div>
                <div className="hidden text-right sm:block">
                  <div className="text-xs font-semibold leading-tight text-khiimori-text-primary">
                    {displayName}
                  </div>
                  <div className="text-[10px] leading-tight text-khiimori-text-muted">
                    {isAuthenticated ? 'Signed in' : 'Guest'}
                  </div>
                </div>
              </div>
              <Button onClick={() => setShowSettings(true)} variant="ghost" size="sm" aria-label="Open settings" className="rounded-full">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="-mb-px mt-4 flex items-center gap-1 border-b border-khiimori-border/5">
            {(
              [
                { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
                { id: 'analytics' as const, label: 'Analytics', icon: TrendingUp },
                { id: 'team' as const, label: 'Team', icon: Users },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-khiimori-text-primary'
                    : 'text-khiimori-text-muted hover:text-khiimori-text-secondary'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {activeTab === tab.id && (
                  <span
                    aria-hidden
                    className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="p-4 sm:p-6">
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Globe} label="Total Tabs" value={stats.totalTabs} color="from-blue-500 to-cyan-600" />
          <StatCard
            icon={Folder}
            label="Tab Groups"
            value={stats.activeGroups}
            color="from-emerald-500 to-teal-600"
            subtitle={`${stats.groupedTabs} tabs in groups`}
          />
          <StatCard
            icon={Zap}
            label="Shared Workspaces"
            value={stats.activeWorkspaces}
            color="from-violet-500 to-purple-600"
            subtitle={`${stats.sessionCount} browser sessions`}
          />
          <StatCard
            icon={Bell}
            label="Notifications"
            value={stats.unreadNotifications}
            color="from-amber-500 to-orange-600"
            highlight={stats.unreadNotifications > 0}
          />
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="overflow-hidden rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 backdrop-blur-xl lg:col-span-2">
              <div className="flex items-center justify-between border-b border-khiimori-border/10 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-emerald-500/15 p-2">
                    <Activity className="h-4 w-4 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="font-semibold leading-tight text-khiimori-text-primary">
                      Activity Feed
                    </h2>
                    <p className="text-[11px] text-khiimori-text-muted">
                      Latest events from your synced tabs
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Refresh activity"
                  onClick={() => void loadDashboard()}
                  className="rounded-full"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="max-h-[28rem] overflow-y-auto p-5">
                <ActivityFeedWidget activities={activities} />
              </div>
            </div>

            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-5 backdrop-blur-xl">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-amber-500/15 p-2">
                  <Zap className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                    Quick Actions
                  </h3>
                  <p className="text-[11px] text-khiimori-text-muted">
                    Get things moving in one click
                  </p>
                </div>
              </div>
              <QuickActionsPanel
                onCreateWorkspace={() => setPromptAction('workspace')}
                onCreateGroup={() => setPromptAction('group')}
                onAutoOrganize={handleAutoOrganize}
                onSyncAll={() => void handleSyncAll()}
              />
            </div>

            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl lg:col-span-3">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-blue-500/15 p-2">
                  <Cloud className="h-4 w-4 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                    Sessions & Workspaces
                  </h3>
                  <p className="text-[11px] text-khiimori-text-muted">
                    Synced sessions and shared workspaces
                  </p>
                </div>
              </div>
              <SessionsSummaryWidget sessions={sessions} sharedWorkspaces={sharedWorkspaces} />
            </div>

            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl lg:col-span-2">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-blue-500/15 p-2">
                  <Globe className="h-4 w-4 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                    Tab Overview
                  </h3>
                  <p className="text-[11px] text-khiimori-text-muted">
                    Recently synced tabs across your groups
                  </p>
                </div>
              </div>
              <TabOverviewWidget
                tabs={filteredTabs}
                groups={uniqueGroupsByName(groups)}
                totalCount={tabs.length}
                bookmarkedTabIds={bookmarkedTabIds}
                bookmarkingTabId={bookmarkingTabId}
                onBookmark={handleBookmarkTab}
                onViewAll={() => navigate('/tabs')}
              />
            </div>

            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-emerald-500/15 p-2">
                  <Network className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                    Integration Status
                  </h3>
                  <p className="text-[11px] text-khiimori-text-muted">
                    Health of every connected service
                  </p>
                </div>
              </div>
              <IntegrationStatusWidget
                wsConnected={wsConnected}
                tabCount={tabs.length}
                groupCount={groups.length}
                isAuthenticated={isAuthenticated}
                hasCollaboration={sharedWorkspaces.length > 0 || collaborators.length > 0}
              />
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <AnalyticsPanel
            loading={analyticsLoading}
            analytics={analytics}
            metrics={metrics}
            score={score}
            isHealthy={isHealthy}
            tabs={tabs}
            onRefresh={() => void loadAnalytics()}
            onOpenInsights={() => navigate('/home?section=insights')}
          />
        )}

        {activeTab === 'team' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
                <div className="mb-5 flex items-center gap-3">
                  <div className="rounded-lg bg-violet-500/15 p-2">
                    <Users className="h-4 w-4 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                      Team Members
                    </h3>
                    <p className="text-[11px] text-khiimori-text-muted">
                      People sharing tabs with you
                    </p>
                  </div>
                  <span className="rounded-full bg-khiimori-bg-elevated/10 px-2.5 py-0.5 text-xs font-medium tabular-nums text-khiimori-text-muted">
                    {collaborators.length}
                  </span>
                </div>
                {collaborators.length > 0 ? (
                  <div className="space-y-2">
                    {collaborators.map((member) => (
                      <div
                        key={member.id}
                        className="group flex items-center gap-3 rounded-xl border border-transparent bg-khiimori-bg-elevated/5 px-3 py-2.5 transition-colors hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10"
                      >
                        <div className="relative shrink-0">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-sm font-semibold text-white">
                            {member.avatar || member.name[0]?.toUpperCase() || '?'}
                          </div>
                          <span
                            aria-hidden
                            className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-khiimori-bg-secondary ${
                              member.status === 'online'
                                ? 'bg-emerald-400'
                                : member.status === 'away'
                                  ? 'bg-amber-400'
                                  : 'bg-khiimori-text-muted/50'
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-medium text-khiimori-text-primary">{member.name}</p>
                          {member.viewing ? (
                            <p className="truncate text-xs text-khiimori-text-muted">Viewing {member.viewing}</p>
                          ) : null}
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ${
                            member.status === 'online'
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : member.status === 'away'
                                ? 'bg-amber-500/20 text-amber-300'
                                : 'bg-khiimori-bg-elevated/20 text-khiimori-text-muted'
                          }`}
                        >
                          {member.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <div className="rounded-full bg-khiimori-bg-elevated/10 p-3">
                      <Users className="h-5 w-5 text-khiimori-text-muted" />
                    </div>
                    <p className="text-sm font-medium text-khiimori-text-secondary">No collaborators yet</p>
                    <p className="max-w-xs text-xs text-khiimori-text-muted">
                      Invite teammates to start sharing tabs and workspaces.
                    </p>
                  </div>
                )}
                <Button variant="ghost" size="sm" className="mt-4 w-full text-violet-400" onClick={() => navigate('/sync')}>
                  Open Sync & Share
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
                <div className="mb-5 flex items-center gap-3">
                  <div className="rounded-lg bg-violet-500/15 p-2">
                    <Cloud className="h-4 w-4 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                      Team Workspaces
                    </h3>
                    <p className="text-[11px] text-khiimori-text-muted">
                      Shared tab collections
                    </p>
                  </div>
                  <span className="rounded-full bg-khiimori-bg-elevated/10 px-2.5 py-0.5 text-xs font-medium tabular-nums text-khiimori-text-muted">
                    {sharedWorkspaces.length}
                  </span>
                </div>
                {sharedWorkspaces.length > 0 ? (
                  <div className="space-y-2">
                    {sharedWorkspaces.slice(0, 8).map((ws) => (
                      <div
                        key={ws.id}
                        className="group flex items-center gap-3 rounded-xl border border-transparent bg-khiimori-bg-elevated/5 px-3 py-2.5 transition-colors hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-500/15">
                          <div className="h-2.5 w-2.5 rounded-full bg-violet-400 ring-2 ring-violet-400/30" />
                        </div>
                        <span className="flex-1 truncate text-sm font-medium text-khiimori-text-primary">
                          {ws.name}
                        </span>
                        <span className="rounded-md bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[11px] tabular-nums text-khiimori-text-muted">
                          {ws.tabCount} tabs
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-khiimori-border/20 bg-khiimori-bg-elevated/5 py-8 text-center">
                    <p className="text-sm text-khiimori-text-muted">No shared workspaces yet</p>
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-emerald-500/15 p-2">
                  <Activity className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                    Activity Feed
                  </h3>
                  <p className="text-[11px] text-khiimori-text-muted">
                    Full timeline of recent events
                  </p>
                </div>
              </div>
              <ActivityFeedWidget activities={activities} fullHeight />
            </div>
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-lg bg-amber-500/15 p-2">
              <Bell className="h-4 w-4 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold leading-tight text-khiimori-text-primary">
                Notifications
              </h3>
              <p className="text-[11px] text-khiimori-text-muted">
                Click to mark as read
              </p>
            </div>
            <span className="rounded-full bg-khiimori-bg-elevated/10 px-2.5 py-0.5 text-xs font-medium tabular-nums text-khiimori-text-muted">
              {stats.unreadNotifications} unread
            </span>
          </div>
          <NotificationWidget notifications={notifications} onMarkRead={handleMarkNotificationRead} />
        </div>
      </main>

      {showSettings ? <SettingsModal onClose={() => setShowSettings(false)} /> : null}

      <PromptDialog
        isOpen={promptAction === 'workspace'}
        onClose={() => setPromptAction(null)}
        onSubmit={handleCreateWorkspace}
        title="Create workspace"
        description="Name your shared workspace."
        defaultValue="New workspace"
        submitText="Create"
      />
      <PromptDialog
        isOpen={promptAction === 'group'}
        onClose={() => setPromptAction(null)}
        onSubmit={handleCreateGroup}
        title="Create tab group"
        description="Group your current session tabs under this name."
        defaultValue="New group"
        submitText="Create"
      />
    </div>
  );
};

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
  highlight?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color, subtitle, highlight }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -2 }}
    transition={{ type: 'spring', stiffness: 300, damping: 24 }}
    className={`group relative overflow-hidden rounded-2xl border border-khiimori-border/10 bg-gradient-to-br from-khiimori-bg-elevated/10 to-khiimori-bg-elevated/0 p-5 backdrop-blur-xl transition-shadow hover:shadow-xl hover:shadow-violet-500/5 ${
      highlight ? 'ring-2 ring-violet-500/60' : ''
    }`}
  >
    {/* Decorative gradient blob in the corner */}
    <div
      className={`pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br ${color} opacity-15 blur-2xl transition-opacity group-hover:opacity-25`}
    />
    <div className="relative">
      <div className="mb-4 flex items-center justify-between">
        <div className={`rounded-xl bg-gradient-to-br p-2.5 shadow-lg ${color}`}>
          <Icon className="h-5 w-5 text-white drop-shadow" />
        </div>
        {subtitle ? (
          <span className="rounded-full bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[11px] font-medium text-khiimori-text-muted">
            {subtitle}
          </span>
        ) : null}
      </div>
      <div className="mb-1 text-3xl font-bold tabular-nums text-khiimori-text-primary">
        {value}
      </div>
      <div className="text-sm font-medium text-khiimori-text-secondary">{label}</div>
    </div>
  </motion.div>
);

interface QuickActionsPanelProps {
  onCreateWorkspace: () => void;
  onCreateGroup: () => void;
  onAutoOrganize: () => void;
  onSyncAll: () => void;
}

const QuickActionsPanel: React.FC<QuickActionsPanelProps> = ({
  onCreateWorkspace,
  onCreateGroup,
  onAutoOrganize,
  onSyncAll,
}) => {
  const actions = [
    {
      id: 'workspace',
      icon: Plus,
      label: 'Create Workspace',
      description: 'Start a shared space',
      gradient: 'from-violet-500/35 to-purple-600/35',
      iconClass: 'text-violet-300',
      onClick: onCreateWorkspace,
    },
    {
      id: 'group',
      icon: Folder,
      label: 'Create Group',
      description: 'Bundle related tabs',
      gradient: 'from-emerald-500/35 to-teal-600/35',
      iconClass: 'text-emerald-300',
      onClick: onCreateGroup,
    },
    {
      id: 'organize',
      icon: Zap,
      label: 'Auto-Organize',
      description: 'Sort tabs by domain',
      gradient: 'from-amber-500/35 to-orange-600/35',
      iconClass: 'text-amber-300',
      onClick: onAutoOrganize,
    },
    {
      id: 'sync',
      icon: Cloud,
      label: 'Sync All',
      description: 'Refresh live data',
      gradient: 'from-blue-500/35 to-cyan-600/35',
      iconClass: 'text-blue-300',
      onClick: onSyncAll,
    },
  ] as const;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            type="button"
            onClick={action.onClick}
            className="group relative flex min-h-[5.5rem] flex-col justify-between overflow-hidden rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/30 p-3.5 text-left transition-all hover:border-violet-500/35 hover:bg-khiimori-bg-elevated/50 hover:shadow-lg hover:shadow-violet-500/5"
          >
            <div className="flex w-full items-start justify-between gap-2">
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${action.gradient}`}
              >
                <Icon className={`h-4 w-4 ${action.iconClass}`} />
              </div>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-khiimori-text-muted/40 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-400" />
            </div>
            <div className="mt-3 min-w-0">
              <div className="text-sm font-semibold leading-tight text-khiimori-text-primary group-hover:text-violet-200">
                {action.label}
              </div>
              <div className="mt-1 text-[11px] leading-snug text-khiimori-text-muted">{action.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

interface SessionsSummaryWidgetProps {
  sessions: SessionRow[];
  sharedWorkspaces: SharedSession[];
}

const SessionsSummaryWidget: React.FC<SessionsSummaryWidgetProps> = ({ sessions, sharedWorkspaces }) => {
  if (sessions.length === 0 && sharedWorkspaces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <div className="rounded-full bg-khiimori-bg-elevated/10 p-3">
          <Cloud className="h-6 w-6 text-khiimori-text-muted" />
        </div>
        <p className="text-sm font-medium text-khiimori-text-secondary">
          No saved sessions or shared workspaces yet
        </p>
        <p className="max-w-md text-xs text-khiimori-text-muted">
          Create a workspace to collaborate, or sync tabs from the extension to save a session.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="rounded-md bg-emerald-500/15 p-1.5">
            <Globe className="h-3.5 w-3.5 text-emerald-400" />
          </div>
          <h4 className="text-sm font-semibold text-khiimori-text-primary">
            Browser sessions
          </h4>
          <span className="rounded-full bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[11px] font-medium text-khiimori-text-muted">
            {sessions.length}
          </span>
        </div>
        <div className="space-y-2">
          {sessions.slice(0, 6).map((session) => (
            <div
              key={session.id}
              className="group flex items-center justify-between rounded-xl border border-transparent bg-khiimori-bg-elevated/5 px-3 py-2.5 transition-colors hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-emerald-500/30 to-teal-600/30">
                  <Globe className="h-3.5 w-3.5 text-emerald-300" />
                </div>
                <span className="truncate text-sm text-khiimori-text-primary">{session.name}</span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {session.is_active ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-medium text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Active
                  </span>
                ) : null}
                <span className="rounded-md bg-khiimori-bg-elevated/10 px-1.5 py-0.5 text-[11px] tabular-nums text-khiimori-text-muted">
                  {session.tab_count ?? 0}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="rounded-md bg-violet-500/15 p-1.5">
            <Users className="h-3.5 w-3.5 text-violet-400" />
          </div>
          <h4 className="text-sm font-semibold text-khiimori-text-primary">
            Shared workspaces
          </h4>
          <span className="rounded-full bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[11px] font-medium text-khiimori-text-muted">
            {sharedWorkspaces.length}
          </span>
        </div>
        <div className="space-y-2">
          {sharedWorkspaces.length > 0 ? (
            sharedWorkspaces.slice(0, 6).map((workspace) => (
              <div
                key={workspace.id}
                className="group flex items-center justify-between rounded-xl border border-transparent bg-khiimori-bg-elevated/5 px-3 py-2.5 transition-colors hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-violet-500/30 to-purple-600/30">
                    <Users className="h-3.5 w-3.5 text-violet-300" />
                  </div>
                  <span className="truncate text-sm text-khiimori-text-primary">{workspace.name}</span>
                </div>
                <span className="shrink-0 rounded-md bg-khiimori-bg-elevated/10 px-1.5 py-0.5 text-[11px] tabular-nums text-khiimori-text-muted">
                  {workspace.tabCount} tabs
                </span>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-khiimori-border/20 bg-khiimori-bg-elevated/5 p-4 text-center text-xs text-khiimori-text-muted">
              No shared workspaces yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface AnalyticsPanelProps {
  loading: boolean;
  analytics: AnalyticsSnapshot | null;
  metrics: ReturnType<typeof usePerformance>['metrics'];
  score: number;
  isHealthy: boolean;
  tabs: DashboardTab[];
  onRefresh: () => void;
  onOpenInsights: () => void;
}

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({
  loading,
  analytics,
  metrics,
  score,
  isHealthy,
  tabs,
  onRefresh,
  onOpenInsights,
}) => {
  const topSites = useMemo(() => {
    const raw =
      analytics && analytics.topSites.length > 0
        ? analytics.topSites
        : domainBreakdownFromTabs(tabs);
    return [...raw].sort((a, b) => b.count - a.count).slice(0, TOP_SITES_LIMIT);
  }, [analytics, tabs]);

  const trendChartData = useMemo(() => {
    if (!analytics?.trends?.length) return [];
    return analytics.trends.map((point) => ({
      name: point.label,
      tabs: point.value,
    }));
  }, [analytics?.trends]);

  const maxSiteCount = Math.max(1, ...topSites.map((s) => s.count));
  const totalSiteCount =
    analytics && analytics.topSites.length > 0
      ? analytics.topSites.length
      : domainBreakdownFromTabs(tabs).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-khiimori-text-muted">Usage and performance for the past week</p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={onOpenInsights}>
            Full insights
          </Button>
        </div>
      </div>

      {loading && !analytics ? (
        <div className="flex items-center justify-center py-16 text-violet-400">
          <Loader2 className="mr-2 h-6 w-6 animate-spin" />
          Loading analytics…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={TrendingUp}
              label="Productivity"
              value={analytics?.productivityScore ?? '—'}
              color="from-violet-500 to-purple-600"
              subtitle="This week"
            />
            <StatCard
              icon={Activity}
              label="Focus time"
              value={analytics ? formatDuration(analytics.focusTimeSeconds) : '—'}
              color="from-emerald-500 to-teal-600"
            />
            <StatCard
              icon={Globe}
              label="Tabs opened"
              value={analytics?.tabsOpened ?? tabs.length}
              color="from-blue-500 to-cyan-600"
            />
            <StatCard
              icon={Database}
              label="Sessions"
              value={analytics?.sessionTotal ?? '—'}
              color="from-amber-500 to-orange-600"
              subtitle="Tracked this week"
            />
          </div>

          <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
            <h3 className="mb-4 font-semibold text-khiimori-text-primary">Performance Metrics</h3>
            <PerformanceWidget expanded metrics={metrics} score={score} isHealthy={isHealthy} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
              <div className="mb-4 flex items-center justify-between gap-2">
                <h3 className="font-semibold text-khiimori-text-primary">Top sites</h3>
                {totalSiteCount > TOP_SITES_LIMIT ? (
                  <span className="text-xs text-khiimori-text-muted">
                    Top {TOP_SITES_LIMIT} of {totalSiteCount}
                  </span>
                ) : null}
              </div>
              {topSites.length > 0 ? (
                <div className="space-y-3">
                  {topSites.map((site) => (
                    <div key={site.domain}>
                      <div className="mb-1 flex justify-between text-sm">
                        <span className="truncate text-khiimori-text-primary">{site.domain}</span>
                        <span className="shrink-0 text-khiimori-text-muted">{site.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-khiimori-bg-elevated/10">
                        <div
                          className="h-2 rounded-full bg-violet-500"
                          style={{ width: `${Math.round((site.count / maxSiteCount) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-khiimori-text-muted">No site data yet</p>
              )}
            </div>

            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
              <h3 className="mb-4 font-semibold text-khiimori-text-primary">Activity trends</h3>
              {trendChartData.length > 0 ? (
                <RechartsBarChart
                  data={trendChartData}
                  xAxisDataKey="name"
                  height={240}
                  colors={['#8b5cf6']}
                  showGrid={false}
                  showLegend={false}
                  showTooltip
                />
              ) : (
                <p className="text-sm text-khiimori-text-muted">Trend data will appear after more browsing activity</p>
              )}
              {analytics?.insights && analytics.insights.length > 0 ? (
                <div className="mt-4 border-t border-khiimori-border/10 pt-4">
                  <h4 className="mb-2 text-sm font-medium text-khiimori-text-primary">Insights</h4>
                  <ul className="space-y-1 text-sm text-khiimori-text-muted">
                    {analytics.insights.slice(0, 4).map((insight, i) => (
                      <li key={i}>• {insight}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ActivityFeedWidget: React.FC<{ activities: ActivityItem[]; fullHeight?: boolean }> = ({
  activities,
  fullHeight,
}) => {
  const list = fullHeight ? activities : activities.slice(0, 12);
  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <div className="rounded-full bg-khiimori-bg-elevated/10 p-3">
          <Activity className="h-6 w-6 text-khiimori-text-muted" />
        </div>
        <p className="text-sm font-medium text-khiimori-text-secondary">No recent activity</p>
        <p className="max-w-xs text-xs text-khiimori-text-muted">
          Open a tab or sync the extension to see timeline events here.
        </p>
      </div>
    );
  }
  return (
    <ol className="relative">
      {/* vertical timeline rail */}
      <span
        aria-hidden
        className="absolute bottom-2 left-[19px] top-2 w-px bg-gradient-to-b from-violet-500/40 via-khiimori-border/20 to-transparent"
      />
      {list.map((activity, idx) => {
        const isLast = idx === list.length - 1;
        const relative = relativeTime(activity.timestamp);
        const fallback = formatActivityTime(activity.timestamp, activity.timeLabel);
        const actor = activity.action.split(' — ')[0];
        const rest = activity.action.includes(' — ')
          ? activity.action.slice(activity.action.indexOf(' — ') + 3)
          : activity.action;
        return (
          <li key={activity.id} className="relative flex gap-3 py-2">
            <div className="relative z-10 mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/20 to-purple-600/20 ring-2 ring-khiimori-bg-secondary">
              <span className="text-sm font-semibold text-violet-200">
                {actor?.[0]?.toUpperCase() || '•'}
              </span>
            </div>
            <div
              className={`min-w-0 flex-1 rounded-lg border border-khiimori-border/10 bg-khiimori-bg-elevated/5 px-3 py-2 transition-colors hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10 ${isLast ? '' : ''}`}
            >
              <p className="truncate text-sm text-khiimori-text-primary">
                {actor && <span className="font-semibold text-violet-200">{actor}</span>}
                {actor && rest ? <span className="text-khiimori-text-muted"> — </span> : null}
                <span>{rest}</span>
              </p>
              <p className="mt-0.5 text-[11px] text-khiimori-text-muted">
                {relative || fallback}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

interface PerformanceWidgetProps {
  expanded?: boolean;
  metrics: ReturnType<typeof usePerformance>['metrics'];
  score: number;
  isHealthy: boolean;
}

const PerformanceWidget: React.FC<PerformanceWidgetProps> = ({ expanded, metrics, score, isHealthy }) => {
  const webVitals = [
    { label: 'FCP', raw: metrics.fcp, unit: 'ms', good: metrics.fcp < 1800 },
    { label: 'LCP', raw: metrics.lcp, unit: 'ms', good: metrics.lcp < 2500 },
    { label: 'FID', raw: metrics.fid, unit: 'ms', good: metrics.fid < 100 },
    { label: 'CLS', raw: metrics.cls, unit: '', good: metrics.cls < 0.1 },
    { label: 'FPS', raw: metrics.fps, unit: '', good: metrics.fps >= 55 },
  ];

  return (
    <div className={expanded ? 'grid grid-cols-1 gap-4 lg:grid-cols-5' : ''}>
      <div className={expanded ? 'lg:col-span-2' : ''}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-3 w-3 rounded-full ${isHealthy ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            <span className="font-medium text-khiimori-text-primary">{isHealthy ? 'Healthy' : 'Needs attention'}</span>
          </div>
          <span className="text-3xl font-bold text-violet-400">{score}%</span>
        </div>
        {expanded ? (
          <div className="mt-4 border-t border-khiimori-border/10 pt-4">
            <div className="mb-2 font-medium text-khiimori-text-primary">Memory usage</div>
            <div className="h-2 w-full rounded-full bg-khiimori-bg-elevated/5">
              <div
                className={`h-2 rounded-full ${
                  metrics.memoryUsage > 0.8 ? 'bg-red-500' : metrics.memoryUsage > 0.6 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(100, metrics.memoryUsage * 100)}%` }}
              />
            </div>
            <span className="mt-1 text-xs text-khiimori-text-muted">
              {Math.round(metrics.memoryUsage * 100)}% of heap used
            </span>
          </div>
        ) : null}
      </div>
      {expanded ? (
        <div className="lg:col-span-3">
          <h4 className="mb-3 font-medium text-khiimori-text-primary">Core Web Vitals</h4>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {webVitals.map((vital) => (
              <div key={vital.label} className="min-w-0 px-1 text-center">
                <div
                  className={`truncate text-xl font-bold tabular-nums ${vital.good ? 'text-emerald-400' : 'text-amber-400'}`}
                  title={`${formatVitalValue(vital.label, vital.raw)}${vital.unit}`}
                >
                  {formatVitalValue(vital.label, vital.raw)}
                  {vital.unit}
                </div>
                <div className="text-xs text-khiimori-text-muted">{vital.label}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface TabOverviewWidgetProps {
  tabs: DashboardTab[];
  groups: TabGroup[];
  totalCount: number;
  bookmarkedTabIds: Set<string>;
  bookmarkingTabId: string | null;
  onBookmark: (tabId: string) => void;
  onViewAll: () => void;
}

const TabOverviewWidget: React.FC<TabOverviewWidgetProps> = ({
  tabs,
  groups,
  totalCount,
  bookmarkedTabIds,
  bookmarkingTabId,
  onBookmark,
  onViewAll,
}) => {
  const tabList = tabs.slice(0, 8);
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-blue-500/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-blue-300">
            {totalCount}
          </span>
          <span className="text-sm text-khiimori-text-muted">tabs synced</span>
        </div>
        <Button variant="ghost" size="sm" className="text-violet-400" onClick={onViewAll}>
          View All
          <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="space-y-2">
        {tabList.length > 0 ? (
          tabList.map((tab) => {
            const domain = domainFromUrl(tab.url);
            return (
              <div
                key={tab.id}
                className="group flex items-center gap-3 rounded-xl border border-transparent bg-khiimori-bg-elevated/5 px-3 py-2.5 transition-all hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-khiimori-bg-elevated/30 to-khiimori-bg-elevated/10 ring-1 ring-khiimori-border/20">
                  {tab.favicon ? (
                    <img src={tab.favicon} alt="" className="h-4 w-4" />
                  ) : (
                    <Globe className="h-4 w-4 text-khiimori-text-muted" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-khiimori-text-primary">
                    {tab.title}
                  </p>
                  <p className="truncate text-xs text-khiimori-text-muted">
                    {domain || tab.url}
                  </p>
                </div>
                {tab.groupName ? (
                  <span
                    className="hidden shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium sm:inline-flex"
                    style={{
                      backgroundColor: `${tab.groupColor || '#8b5cf6'}26`,
                      color: tab.groupColor || '#c4b5fd',
                      border: `1px solid ${tab.groupColor || '#8b5cf6'}40`,
                    }}
                  >
                    {tab.groupName}
                  </span>
                ) : null}
                <button
                  type="button"
                  title={bookmarkedTabIds.has(tab.id) ? 'Bookmarked' : 'Save to bookmarks'}
                  disabled={bookmarkingTabId === tab.id}
                  onClick={() => onBookmark(tab.id)}
                  className="rounded-md p-1.5 transition-colors hover:bg-khiimori-bg-tertiary disabled:opacity-50"
                >
                  <Star
                    size={14}
                    className={
                      bookmarkedTabIds.has(tab.id)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-khiimori-text-muted group-hover:text-khiimori-text-secondary'
                    }
                  />
                </button>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-khiimori-border/20 bg-khiimori-bg-elevated/5 py-8 text-center">
            <Globe className="h-6 w-6 text-khiimori-text-muted" />
            <p className="text-sm font-medium text-khiimori-text-secondary">
              No tabs synced yet
            </p>
            <p className="max-w-xs text-xs text-khiimori-text-muted">
              Open the browser extension and sync to see your tabs here.
            </p>
          </div>
        )}
      </div>
      {groups.length > 0 ? (
        <div className="mt-5 border-t border-khiimori-border/10 pt-4">
          <div className="mb-3 flex items-center gap-2">
            <Folder className="h-3.5 w-3.5 text-khiimori-text-muted" />
            <h4 className="text-sm font-semibold text-khiimori-text-primary">Groups</h4>
            <span className="rounded-full bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[11px] font-medium text-khiimori-text-muted">
              {groups.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {groups.slice(0, 6).map((group) => (
              <div
                key={group.id}
                className="group inline-flex items-center gap-2 rounded-full border px-3 py-1 transition-colors"
                style={{
                  backgroundColor: `${group.color}15`,
                  borderColor: `${group.color}40`,
                }}
              >
                <div
                  className="h-2 w-2 rounded-full ring-2 ring-white/10"
                  style={{ backgroundColor: group.color }}
                />
                <span className="text-xs font-medium text-khiimori-text-primary">
                  {group.name}
                </span>
                <span className="rounded bg-black/20 px-1.5 text-[10px] tabular-nums text-khiimori-text-muted">
                  {group.tabCount ?? group.tabs?.length ?? 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface IntegrationStatusWidgetProps {
  wsConnected: boolean;
  tabCount: number;
  groupCount: number;
  isAuthenticated: boolean;
  hasCollaboration: boolean;
}

const IntegrationStatusWidget: React.FC<IntegrationStatusWidgetProps> = ({
  wsConnected,
  tabCount,
  groupCount,
  isAuthenticated,
  hasCollaboration,
}) => {
  const voiceAvailable =
    typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const integrations = [
    {
      name: 'Tab Memory',
      description: tabCount > 0 ? `${tabCount} tabs indexed` : 'Awaiting sync',
      status: tabCount > 0 ? ('active' as const) : ('inactive' as const),
      icon: Database,
      tone: 'emerald',
    },
    {
      name: 'Live sync',
      description: wsConnected ? 'WebSocket connected' : 'Offline',
      status: wsConnected ? ('active' as const) : ('inactive' as const),
      icon: Network,
      tone: 'violet',
    },
    {
      name: 'AI Assistant',
      description: isAuthenticated ? 'Signed in' : 'Sign in to enable',
      status: isAuthenticated ? ('active' as const) : ('inactive' as const),
      icon: Zap,
      tone: 'amber',
    },
    {
      name: 'Voice Commands',
      description: voiceAvailable ? 'Microphone ready' : 'Not supported',
      status: voiceAvailable ? ('active' as const) : ('inactive' as const),
      icon: Mic,
      tone: 'cyan',
    },
    {
      name: 'Collaboration',
      description: hasCollaboration ? 'Workspaces active' : 'No collaborators',
      status: hasCollaboration ? ('active' as const) : ('inactive' as const),
      icon: Users,
      tone: 'pink',
    },
    {
      name: 'Smart Groups',
      description: groupCount > 0 ? `${groupCount} groups` : 'No groups yet',
      status: groupCount > 0 ? ('active' as const) : ('inactive' as const),
      icon: Folder,
      tone: 'blue',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {integrations.map((integration) => {
        const Icon = integration.icon;
        const isActive = integration.status === 'active';
        return (
          <div
            key={integration.name}
            className={`group relative overflow-hidden rounded-xl border p-3 transition-all ${
              isActive
                ? 'border-khiimori-border/10 bg-khiimori-bg-elevated/5 hover:border-violet-500/30'
                : 'border-dashed border-khiimori-border/15 bg-khiimori-bg-elevated/3'
            }`}
          >
            {isActive && (
              <span
                aria-hidden
                className="pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full bg-emerald-500/10 blur-2xl"
              />
            )}
            <div className="relative flex items-start gap-2.5">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  isActive
                    ? `bg-gradient-to-br from-${integration.tone}-500/30 to-${integration.tone}-600/20`
                    : 'bg-khiimori-bg-elevated/10'
                }`}
                style={
                  isActive
                    ? undefined
                    : undefined
                }
              >
                <Icon
                  className={`h-4 w-4 ${
                    isActive ? 'text-emerald-300' : 'text-khiimori-text-muted'
                  }`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-khiimori-text-primary">
                    {integration.name}
                  </span>
                  <span
                    aria-hidden
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                      isActive
                        ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]'
                        : 'bg-khiimori-text-muted/40'
                    }`}
                    title={isActive ? 'Active' : 'Inactive'}
                  />
                </div>
                <p className="truncate text-[11px] text-khiimori-text-muted">
                  {integration.description}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface NotificationWidgetProps {
  notifications: TabNotification[];
  onMarkRead: (id: string) => void;
}

const NotificationWidget: React.FC<NotificationWidgetProps> = ({ notifications, onMarkRead }) => {
  const unread = notifications.filter((n) => !n.read).slice(0, 8);
  if (unread.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <div className="rounded-full bg-emerald-500/10 p-3">
          <Bell className="h-6 w-6 text-emerald-400" />
        </div>
        <p className="text-sm font-medium text-khiimori-text-secondary">All caught up</p>
        <p className="text-xs text-khiimori-text-muted">No new notifications</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {unread.map((notification) => {
        const tone =
          notification.type === 'error'
            ? { ring: 'bg-red-500/10 text-red-400', bar: 'bg-red-500/50' }
            : notification.type === 'warning'
              ? { ring: 'bg-amber-500/10 text-amber-400', bar: 'bg-amber-500/50' }
              : notification.type === 'success'
                ? { ring: 'bg-emerald-500/10 text-emerald-400', bar: 'bg-emerald-500/50' }
                : { ring: 'bg-blue-500/10 text-blue-400', bar: 'bg-blue-500/50' };
        return (
          <button
            key={notification.id}
            type="button"
            className="group relative w-full overflow-hidden rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-3 pl-4 text-left transition-all hover:border-violet-500/20 hover:bg-khiimori-bg-elevated/10"
            onClick={() => onMarkRead(notification.id)}
          >
            <span
              aria-hidden
              className={`absolute left-0 top-0 h-full w-1 ${tone.bar}`}
            />
            <div className="flex items-start gap-2.5">
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tone.ring}`}
              >
                <AlertCircle className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="truncate text-sm font-medium text-khiimori-text-primary">
                    {notification.title}
                  </p>
                  {notification.created_at && (
                    <span className="shrink-0 text-[10px] tabular-nums text-khiimori-text-muted">
                      {relativeTime(notification.created_at)}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-khiimori-text-muted">
                  {notification.message}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

interface SettingsModalProps {
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ onClose }) => {
  const enterpriseSettings = useAppStore((state) => state.enterpriseSettings);
  const updateEnterpriseSettings = useAppStore((state) => state.updateEnterpriseSettings);
  const { setTheme, resolvedTheme } = useTheme();

  const integrations = [
    { id: 'darkMode', name: 'Dark Mode', description: 'Use dark theme' },
    { id: 'autoSave', name: 'Auto-save', description: 'Automatically save changes' },
    { id: 'realtimeSync', name: 'Real-time Sync', description: 'Sync changes in real-time' },
    { id: 'aiSuggestions', name: 'AI Suggestions', description: 'Enable AI-powered suggestions' },
    { id: 'proactiveMode', name: 'Proactive Mode', description: 'AI proactively suggests actions' },
    { id: 'performanceMonitoring', name: 'Performance Monitoring', description: 'Track Core Web Vitals' },
  ];

  const toggleSetting = (id: string) => {
    const newValue = !enterpriseSettings[id as keyof typeof enterpriseSettings];
    updateEnterpriseSettings({ [id]: newValue });
    if (id === 'darkMode') setTheme(newValue ? 'dark' : 'light');
  };

  return (
    <Modal isOpen onClose={onClose} title="Dashboard preferences" size="lg">
      <div className="space-y-4">
        {integrations.map((setting) => (
          <div key={setting.id} className="flex items-center justify-between rounded-xl bg-khiimori-bg-elevated/5 p-4">
            <div>
              <h4 className="font-medium text-khiimori-text-primary">{setting.name}</h4>
              <p className="text-sm text-khiimori-text-muted">{setting.description}</p>
            </div>
            <button
              type="button"
              onClick={() => toggleSetting(setting.id)}
              className={`relative h-6 w-12 rounded-full transition-colors shrink-0 ${
                (setting.id === 'darkMode' ? resolvedTheme === 'dark' : enterpriseSettings[setting.id as keyof typeof enterpriseSettings])
                  ? 'bg-violet-500'
                  : 'bg-khiimori-bg-tertiary'
              }`}
              aria-label={`Toggle ${setting.name}`}
              aria-pressed={setting.id === 'darkMode' ? resolvedTheme === 'dark' : !!enterpriseSettings[setting.id as keyof typeof enterpriseSettings]}
            >
              <div
                className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                  (setting.id === 'darkMode' ? resolvedTheme === 'dark' : enterpriseSettings[setting.id as keyof typeof enterpriseSettings])
                    ? 'left-7'
                    : 'left-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-3 border-t border-khiimori-border/10 pt-6">
        <Button onClick={onClose} variant="primary" className="flex-1">
          Done
        </Button>
      </div>
    </Modal>
  );
};

export default EnterpriseDashboard;
